/**
 * Карточка накладной — как «Открыть» в боте (§11.2, §11.3 ТЗ `ops`).
 *
 * Кнопки — из `actions` сервера, те же, что в карточке бота: «Оплачено
 * полностью», «Оплатить частично», «Изменить срок»; в истории — «Отменить
 * отметку», только тому, кто её поставил, или владельцу. Подтверждение
 * крупной суммы спрашивается выше того же порога, что в боте, — сервер
 * без подтверждения всё равно откажет.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError } from '@shell/api';
import { ErrorState, Loading } from '@shell/states';
import { rub } from '../../api';
import { Notice, Pill, isoDate, when } from '../../ui';
import { LIVE, paymentsApi } from './api';
import type { CardAnswer, InvoiceCard as CardData, PaymentMark } from './api';

export function cardKey(id: number) {
  return ['payments', 'card', id] as const;
}

type Mode = null | 'full' | 'partial' | 'due' | { cancel: number };

export function InvoiceCard({ id }: { id: number }) {
  const client = useQueryClient();
  const [mode, setMode] = useState<Mode>(null);
  const [amount, setAmount] = useState('');
  const [due, setDue] = useState('');
  const [reason, setReason] = useState('');
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);

  const query = useQuery<CardData, ApiError>({
    queryKey: cardKey(id),
    queryFn: () => paymentsApi.card(id),
    ...LIVE,
  });

  const done = {
    onSuccess: (answer: CardAnswer) => {
      client.setQueryData(cardKey(id), answer.card);
      setResult({ ok: true, text: answer.message });
      setMode(null);
      setAmount('');
      setReason('');
    },
    onError: (error: ApiError) =>
      setResult({ ok: false, text: error.detail ?? 'Не получилось. Попробуйте ещё раз.' }),
    onSettled: () => client.invalidateQueries({ queryKey: ['payments'] }),
  };

  const pay = useMutation<CardAnswer, ApiError, { amount?: string; confirmed?: boolean }>({
    mutationFn: (body) => paymentsApi.pay(id, body),
    ...done,
  });
  const changeDue = useMutation<CardAnswer, ApiError, void>({
    mutationFn: () => paymentsApi.changeDue(id, { due_date: due, reason }),
    ...done,
  });
  const cancel = useMutation<CardAnswer, ApiError, number>({
    mutationFn: (paymentId) => paymentsApi.cancelMark(paymentId, reason),
    ...done,
  });

  if (query.isPending) return <Loading />;
  if (query.error) {
    return query.error.status === 404 ? (
      <ErrorState title="Накладной нет" text="Её могли удалить в StoreHouse. Вернитесь к списку." />
    ) : (
      <ErrorState error={query.error} onRetry={() => query.refetch()} />
    );
  }

  const card = query.data;
  const invoice = card.invoice;
  const busy = pay.isPending || changeDue.isPending || cancel.isPending;
  const open = (next: Mode) => {
    setResult(null);
    setMode(next);
  };

  return (
    <article className="pay-card invoice-card" aria-label="Накладная">
      <header className="pay-card__head">
        <div>
          <h2 className="pay-card__title">
            Накладная {invoice.number} от {isoDate(invoice.doc_date)}
          </h2>
          <p className="faint">
            {invoice.supplier.name} · внешний номер: {invoice.external_number ?? 'не указан'}
          </p>
        </div>
        <Pill colour={invoice.is_settled ? 'green' : invoice.overdue_days > 0 ? 'red' : null}>
          {invoice.status}
        </Pill>
      </header>

      <dl className="invoice-facts">
        <div>
          <dt>Сумма</dt>
          <dd>{rub(invoice.total)}</dd>
        </div>
        {invoice.vat && (
          <div>
            <dt>в т. ч. НДС</dt>
            <dd>{rub(invoice.vat)}</dd>
          </div>
        )}
        {invoice.adjusted !== '0.00' && (
          <div>
            <dt>Корректировки</dt>
            <dd>{rub(invoice.adjusted)}</dd>
          </div>
        )}
        <div>
          <dt>Остаток</dt>
          <dd>
            <strong>{invoice.is_posted ? rub(invoice.balance) : '—'}</strong>
          </dd>
        </div>
        <div>
          <dt>Срок оплаты</dt>
          <dd>
            {invoice.due_date ? isoDate(invoice.due_date) : 'появится, когда документ проведут'}
            {invoice.due_override && (
              <span className="faint">
                {' '}
                · изменён вручную{invoice.due_override.reason ? `: ${invoice.due_override.reason}` : ''}
              </span>
            )}
          </dd>
        </div>
        {invoice.is_posted && (
          <div>
            <dt>Просрочено</dt>
            <dd>{invoice.overdue_days > 0 ? `да, ${invoice.overdue_days} дн.` : 'нет'}</dd>
          </div>
        )}
        <div>
          <dt>Источник</dt>
          <dd>{invoice.source}</dd>
        </div>
      </dl>

      {!invoice.is_posted && (
        <p className="notice">
          Документ внесён в StoreHouse, но ещё не проведён: отметить оплату нельзя, и в сумму к
          оплате он не входит.
        </p>
      )}

      {result && <Notice kind={result.ok ? 'ok' : 'error'}>{result.text}</Notice>}

      {card.actions.length > 0 && mode === null && (
        <div className="order__buttons">
          {card.actions.includes('pay_full') && (
            <button
              type="button"
              className="button button--primary button--small"
              disabled={busy}
              onClick={() => (card.needs_confirmation ? open('full') : pay.mutate({}))}
            >
              Оплачено полностью
            </button>
          )}
          {card.actions.includes('pay_partial') && (
            <button type="button" className="button button--small" onClick={() => open('partial')}>
              Оплатить частично
            </button>
          )}
          {card.actions.includes('change_due') && (
            <button type="button" className="button button--small" onClick={() => open('due')}>
              Изменить срок
            </button>
          )}
        </div>
      )}

      {mode === 'full' && (
        <div className="order__confirm">
          <p>
            Отметить оплату {rub(invoice.balance)} поставщику «{invoice.supplier.name}»? Сумма выше
            порога {rub(card.threshold)} — отметка попадёт в журнал.
          </p>
          <div className="order__buttons">
            <button type="button" className="button" onClick={() => setMode(null)}>
              Отмена
            </button>
            <button
              type="button"
              className="button button--primary"
              disabled={busy}
              onClick={() => pay.mutate({ confirmed: true })}
            >
              Да, отметить оплату
            </button>
          </div>
        </div>
      )}

      {mode === 'partial' && (
        <form
          className="order__confirm"
          onSubmit={(event) => {
            event.preventDefault();
            pay.mutate({ amount });
          }}
        >
          <label className="pay-field">
            <span>Какую сумму отметить</span>
            <input
              className="input"
              inputMode="decimal"
              placeholder="12400,50"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
            />
          </label>
          <div className="order__buttons">
            <button type="button" className="button" onClick={() => setMode(null)}>
              Отмена
            </button>
            <button type="submit" className="button button--primary" disabled={busy || !amount.trim()}>
              Отметить
            </button>
          </div>
        </form>
      )}

      {mode === 'due' && (
        <form
          className="order__confirm"
          onSubmit={(event) => {
            event.preventDefault();
            changeDue.mutate();
          }}
        >
          <label className="pay-field">
            <span>Новый срок</span>
            <input className="input" type="date" value={due} onChange={(event) => setDue(event.target.value)} />
          </label>
          <label className="pay-field">
            <span>Причина — без неё срок нечем объяснить</span>
            <input
              className="input"
              placeholder="договорились по телефону"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          </label>
          <div className="order__buttons">
            <button type="button" className="button" onClick={() => setMode(null)}>
              Отмена
            </button>
            <button
              type="submit"
              className="button button--primary"
              disabled={busy || !due || !reason.trim()}
            >
              Изменить срок
            </button>
          </div>
        </form>
      )}

      <History
        card={card}
        cancelling={typeof mode === 'object' && mode !== null ? mode.cancel : null}
        reason={reason}
        busy={busy}
        onReason={setReason}
        onAsk={(paymentId) => open({ cancel: paymentId })}
        onCancel={(paymentId) => cancel.mutate(paymentId)}
        onClose={() => setMode(null)}
      />
    </article>
  );
}

function History({
  card,
  cancelling,
  reason,
  busy,
  onReason,
  onAsk,
  onCancel,
  onClose,
}: {
  card: CardData;
  cancelling: number | null;
  reason: string;
  busy: boolean;
  onReason: (value: string) => void;
  onAsk: (paymentId: number) => void;
  onCancel: (paymentId: number) => void;
  onClose: () => void;
}) {
  if (card.payments.length === 0 && card.adjustments.length === 0) {
    return <p className="faint">По этой накладной ещё ничего не отмечали.</p>;
  }
  return (
    <div className="panel__block">
      <h3 className="block-title">История</h3>
      <ul className="pay-history">
        {card.payments.map((payment) => (
          <HistoryMark
            key={payment.id}
            payment={payment}
            asking={cancelling === payment.id}
            reason={reason}
            busy={busy}
            onReason={onReason}
            onAsk={() => onAsk(payment.id)}
            onCancel={() => onCancel(payment.id)}
            onClose={onClose}
          />
        ))}
        {card.adjustments.map((adjustment) => (
          <li key={`a${adjustment.id}`} className="pay-history__item">
            <span>
              {isoDate(adjustment.created_at)} · {adjustment.kind_title} · {rub(adjustment.amount)}
            </span>
            <span className="faint">{adjustment.reason}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function HistoryMark({
  payment,
  asking,
  reason,
  busy,
  onReason,
  onAsk,
  onCancel,
  onClose,
}: {
  payment: PaymentMark;
  asking: boolean;
  reason: string;
  busy: boolean;
  onReason: (value: string) => void;
  onAsk: () => void;
  onCancel: () => void;
  onClose: () => void;
}) {
  return (
    <li className={`pay-history__item${payment.cancelled ? ' pay-history__item--cancelled' : ''}`}>
      <span>
        {isoDate(payment.paid_on)} · {rub(payment.amount)} · {payment.marked_by ?? '—'} ·{' '}
        {payment.cancelled ? 'отменена' : 'действует'}
      </span>
      <span className="faint">
        отмечено {when(payment.marked_at)}
        {payment.cancelled && payment.cancel_reason ? ` · причина отмены: ${payment.cancel_reason}` : ''}
      </span>
      {payment.can_cancel && !asking && (
        <button type="button" className="link-button" onClick={onAsk}>
          Отменить отметку
        </button>
      )}
      {asking && (
        <form
          className="order__confirm"
          onSubmit={(event) => {
            event.preventDefault();
            onCancel();
          }}
        >
          <label className="pay-field">
            <span>Почему отменяем? Причина останется в истории</span>
            <input className="input" value={reason} onChange={(event) => onReason(event.target.value)} />
          </label>
          <div className="order__buttons">
            <button type="button" className="button" onClick={onClose}>
              Не отменять
            </button>
            <button type="submit" className="button button--primary" disabled={busy || !reason.trim()}>
              Отменить отметку
            </button>
          </div>
        </form>
      )}
    </li>
  );
}
